import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, CheckCircle, AlertCircle, ChevronRight, Sparkles, Heart, Brain, Battery, Users, Moon, Briefcase, TrendingDown, MessageSquare, Send } from 'lucide-react';
import { userService } from '../../services/userService';
import { userFactsService } from '../../services/userFactsService';
import { useAuth } from '../../context/AuthContext';
import { WELLBEING_METRICS } from '../../utils/wellbeingDataProcessor';

interface MoodSetupModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

interface WellbeingFactor {
  key: string;
  label: string;
  question: string;
  icon: React.ReactNode;
  lowLabel: string;
  highLabel: string;
  color: string;
  invertedScale?: boolean;
}

// Map centralized config to UI-specific properties (icons and Tailwind color names)
const WELLBEING_FACTORS: WellbeingFactor[] = WELLBEING_METRICS.map((metric) => {
  // Map icons based on metric key
  const iconMap: Record<string, React.ReactNode> = {
    wellbeing_mood_state: <Heart className="w-5 h-5" />,
    wellbeing_stress_level: <TrendingDown className="w-5 h-5" />,
    wellbeing_work_life_balance: <Briefcase className="w-5 h-5" />,
    wellbeing_focus: <Brain className="w-5 h-5" />,
    wellbeing_energy: <Battery className="w-5 h-5" />,
    wellbeing_social_support: <Users className="w-5 h-5" />,
    wellbeing_sleep: <Moon className="w-5 h-5" />,
  };

  // Map hex colors to Tailwind color names
  const colorMap: Record<string, string> = {
    '#ef4444': 'blue',      // mood (but using blue for UI)
    '#f59e0b': 'orange',    // stress
    '#84cc16': 'teal',      // work-life
    '#3b82f6': 'purple',    // focus
    '#06b6d4': 'green',     // energy
    '#10b981': 'pink',      // social
    '#8b5cf6': 'indigo',    // sleep
  };

  return {
    key: metric.key,
    label: metric.label,
    question: metric.question,
    icon: iconMap[metric.key] || <Heart className="w-5 h-5" />,
    lowLabel: metric.lowLabel,
    highLabel: metric.highLabel,
    color: colorMap[metric.color] || 'blue',
    invertedScale: metric.invertedScale,
  };
});

const COLOR_CLASSES: Record<string, { bg: string; border: string; text: string; ring: string; gradient: string }> = {
  blue: { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-600', ring: 'ring-blue-500', gradient: 'from-blue-400 to-blue-600' },
  orange: { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-600', ring: 'ring-orange-500', gradient: 'from-orange-400 to-orange-600' },
  teal: { bg: 'bg-teal-500', border: 'border-teal-500', text: 'text-teal-600', ring: 'ring-teal-500', gradient: 'from-teal-400 to-teal-600' },
  purple: { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-600', ring: 'ring-purple-500', gradient: 'from-purple-400 to-purple-600' },
  green: { bg: 'bg-green-500', border: 'border-green-500', text: 'text-green-600', ring: 'ring-green-500', gradient: 'from-green-400 to-green-600' },
  pink: { bg: 'bg-pink-500', border: 'border-pink-500', text: 'text-pink-600', ring: 'ring-pink-500', gradient: 'from-pink-400 to-pink-600' },
  indigo: { bg: 'bg-indigo-500', border: 'border-indigo-500', text: 'text-indigo-600', ring: 'ring-indigo-500', gradient: 'from-indigo-400 to-indigo-600' }
};

export const MoodSetupModal: React.FC<MoodSetupModalProps> = ({ isOpen, onComplete }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [showAdditionalFactors, setShowAdditionalFactors] = useState(false);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [completedFactors, setCompletedFactors] = useState<Record<string, number>>({});
  const [savingFactor, setSavingFactor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [additionalInfoText, setAdditionalInfoText] = useState('');
  const [savingAdditionalInfo, setSavingAdditionalInfo] = useState(false);
  const [additionalInfoSaved, setAdditionalInfoSaved] = useState(false);
  const [loadingAdditionalInfo, setLoadingAdditionalInfo] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setShowAdditionalFactors(false);
      setShowAdditionalInfo(false);
      setCompletedFactors({});
      setSavingFactor(null);
      setError(null);
      setAdditionalInfoText('');
      setSavingAdditionalInfo(false);
      setAdditionalInfoSaved(false);
      setLoadingAdditionalInfo(false);
    }
  }, [isOpen]);

  // Fetch existing wellbeing_additional_info when modal opens
  useEffect(() => {
    const fetchExistingInfo = async () => {
      if (!isOpen || !user?.id) return;
      
      setLoadingAdditionalInfo(true);
      try {
        const factResponse = await userFactsService.getUserFact('wellbeing_additional_info');
        if (factResponse?.fact?.value && typeof factResponse.fact.value === 'string') {
          setAdditionalInfoText(factResponse.fact.value);
        }
      } catch (err) {
        // Silently fail - it's okay if there's no existing info
        console.debug('No existing wellbeing_additional_info found');
      } finally {
        setLoadingAdditionalInfo(false);
      }
    };

    fetchExistingInfo();
  }, [isOpen, user?.id]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const saveWellbeingFact = useCallback(async (key: string, value: number) => {
    if (!user?.id) {
      setError('User not authenticated');
      return false;
    }

    setSavingFactor(key);
    setError(null);

    try {
      if (key === 'wellbeing_mood_state') {
        await userService.updateMoodInUserFacts(value, user.id);
      } else {
        await userService.updateWellbeingFact(key, value, user.id);
      }
      setCompletedFactors(prev => ({ ...prev, [key]: value }));
      return true;
    } catch (err: any) {
      const errorMessage = err.message || `Failed to save ${key}`;
      setError(errorMessage);
      return false;
    } finally {
      setSavingFactor(null);
    }
  }, [user?.id]);

  const handleFactorSelect = async (value: number) => {
    const currentFactor = WELLBEING_FACTORS[currentStep];
    const success = await saveWellbeingFact(currentFactor.key, value);
    
    if (success) {
      // If this was the initial mood and they selected a value, show additional factors option
      if (currentStep === 0) {
        // Short delay to show the saved state, then ask about additional factors
        setTimeout(() => {
          setShowAdditionalFactors(true);
        }, 800);
      } else {
        // For other factors, move to next or show additional info screen
        setTimeout(() => {
          if (currentStep < WELLBEING_FACTORS.length - 1) {
            setCurrentStep(prev => prev + 1);
          } else {
            // Last factor done - show additional info screen
            setShowAdditionalInfo(true);
          }
        }, 600);
      }
    }
  };

  const handleSkipFactor = () => {
    if (currentStep === 0) {
      // Skipping initial mood - exit without asking for more
      onComplete();
    } else {
      // Skip to next factor or show additional info screen
      if (currentStep < WELLBEING_FACTORS.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        // Last factor skipped - show additional info screen
        setShowAdditionalInfo(true);
      }
    }
  };

  const handleSaveAdditionalInfo = async () => {
    if (!user?.id || !additionalInfoText.trim()) return;
    
    setSavingAdditionalInfo(true);
    setError(null);
    
    try {
      await userService.updateWellbeingTextFact('wellbeing_additional_info', additionalInfoText.trim(), user.id);
      setAdditionalInfoSaved(true);
      setTimeout(() => onComplete(), 1000);
    } catch (err: any) {
      setError(err.message || 'Failed to save additional information');
      setSavingAdditionalInfo(false);
    }
  };

  const handleSkipAdditionalInfo = () => {
    onComplete();
  };

  const handleContinueWithMore = () => {
    setShowAdditionalFactors(false);
    setCurrentStep(1); // Move to stress level
  };

  const handleFinishNow = () => {
    onComplete();
  };

  const handleClose = () => {
    // Always exit the modal when clicking the X button
    onComplete();
  };

  if (!isOpen) return null;

  // Calculate completed count for use in multiple screens
  const completedCount = Object.keys(completedFactors).length;

  // Render the additional info text input screen
  if (showAdditionalInfo) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />

          <div className="inline-block align-bottom bg-white rounded-2xl px-6 pt-6 pb-6 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
            {/* Close button */}
            <div className="absolute top-0 right-0 pt-4 pr-4">
              <button
                type="button"
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={handleSkipAdditionalInfo}
                disabled={savingAdditionalInfo}
              >
                <span className="sr-only">Close</span>
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center text-white shadow-md">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-medium text-cyan-600 uppercase tracking-wider">
                  One Last Thing
                </span>
                <h3 className="text-lg leading-6 font-semibold text-gray-900">
                  Anything else to share?
                </h3>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Is there anything else that might help me personalize your experience? You can update it later. 
            </p>

            {/* Text input */}
            <div className="relative">
              <textarea
                value={additionalInfoText}
                onChange={(e) => setAdditionalInfoText(e.target.value)}
                placeholder="E.g., I like concise responses, I respond better to encouragement, I prefer morning workouts ..."
                className="w-full h-32 px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                disabled={savingAdditionalInfo || additionalInfoSaved || loadingAdditionalInfo}
                maxLength={500}
              />
              {loadingAdditionalInfo ? (
                <div className="absolute bottom-2 right-2 flex items-center gap-1.5 text-xs text-cyan-600">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Loading...</span>
                </div>
              ) : (
                <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                  {additionalInfoText.length}/500
                </div>
              )}
            </div>

            {/* Feedback Messages */}
            {savingAdditionalInfo && (
              <div className="flex items-center justify-center space-x-2 text-cyan-600 text-sm mt-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </div>
            )}
            
            {additionalInfoSaved && !savingAdditionalInfo && (
              <div className="flex items-center justify-center space-x-2 text-green-600 text-sm mt-4">
                <CheckCircle className="w-4 h-4" />
                <span>Saved! Thank you for sharing.</span>
              </div>
            )}
            
            {error && !savingAdditionalInfo && (
              <div className="flex items-center justify-center space-x-2 text-red-600 text-sm mt-4">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}

            {/* Action Buttons */}
            {!additionalInfoSaved && (
              <div className="mt-6 flex flex-col gap-3">
                {additionalInfoText.trim() && (
                  <button
                    onClick={handleSaveAdditionalInfo}
                    disabled={savingAdditionalInfo || loadingAdditionalInfo}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white py-3 px-4 rounded-xl font-medium hover:from-cyan-600 hover:to-cyan-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                    Save & Finish
                  </button>
                )}
                <button
                  onClick={handleSkipAdditionalInfo}
                  disabled={savingAdditionalInfo || loadingAdditionalInfo}
                  className="w-full py-2.5 px-4 text-gray-600 text-sm hover:text-gray-800 transition-colors disabled:opacity-50"
                >
                  {additionalInfoText.trim() ? 'Skip without saving' : 'Skip this step'}
                </button>
              </div>
            )}

            {/* Completed factors summary */}
            {completedCount > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 text-center mb-2">Your check-in summary</p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {WELLBEING_FACTORS.map(factor => {
                    const value = completedFactors[factor.key];
                    const factorColors = COLOR_CLASSES[factor.color];
                    if (value === undefined) return null;
                    return (
                      <div 
                        key={factor.key}
                        className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${factorColors.text} bg-gray-50`}
                      >
                        {factor.icon}
                        <span>{value}/10</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const currentFactor = WELLBEING_FACTORS[currentStep];
  const colorClasses = COLOR_CLASSES[currentFactor.color];
  const isCurrentSaving = savingFactor === currentFactor.key;
  const isCurrentSaved = completedFactors[currentFactor.key] !== undefined;

  // Render the "Want to share more?" transition screen
  if (showAdditionalFactors && currentStep === 0) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />

          <div className="inline-block align-bottom bg-white rounded-2xl px-6 pt-6 pb-6 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
            {/* Success indicator */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Great! Mood saved ✨
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Want to help us understand you even better? A quick 30-second check-in on a few more areas can help provide more personalized support.
              </p>

              {/* Preview of additional factors */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex flex-wrap justify-center gap-2">
                  {WELLBEING_FACTORS.slice(1).map((factor, idx) => (
                    <div 
                      key={factor.key}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs text-gray-600 ${
                        idx < 3 ? 'opacity-100' : 'opacity-60'
                      }`}
                    >
                      <span className={COLOR_CLASSES[factor.color].text}>{factor.icon}</span>
                      <span>{factor.label}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Skip any you prefer not to answer
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleContinueWithMore}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
                >
                  <Sparkles className="w-4 h-4" />
                  Quick check-in (30 sec)
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={handleFinishNow}
                  className="w-full py-2.5 px-4 text-gray-600 text-sm hover:text-gray-800 transition-colors"
                >
                  I'm good for now, thanks!
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6">
          {/* Close button */}
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleClose}
              disabled={isCurrentSaving}
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Progress indicator (only show after initial mood) */}
          {currentStep > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">
                  {currentStep} of {WELLBEING_FACTORS.length - 1} additional
                </span>
                <span className="text-xs text-gray-500">
                  {completedCount} saved
                </span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${colorClasses.gradient} transition-all duration-500`}
                  style={{ width: `${(currentStep / (WELLBEING_FACTORS.length - 1)) * 100}%` }}
                />
              </div>
            </div>
          )}

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              {/* Factor icon and title */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorClasses.gradient} flex items-center justify-center text-white shadow-md`}>
                  {currentFactor.icon}
                </div>
                <div>
                  <span className={`text-xs font-medium ${colorClasses.text} uppercase tracking-wider`}>
                    {currentFactor.label}
                  </span>
                  <h3 className="text-lg leading-6 font-semibold text-gray-900">
                    {currentFactor.question}
                  </h3>
                </div>
              </div>
              
              <div className="mt-4">
                {currentStep === 0 && (
                  <p className="text-sm text-gray-600 mb-6">
                    Help us understand your current state to provide better support. Simply click a number to save.
                  </p>
                )}

                {/* Inverted scale hint for stress */}
                {currentFactor.invertedScale && (
                  <div className="mb-4 flex items-center justify-center gap-2 text-sm text-orange-600 bg-orange-50 rounded-lg py-2 px-3">
                    <TrendingDown className="w-4 h-4" />
                    <span>For stress, lower is better!</span>
                  </div>
                )}

                {/* Rating Selection */}
                <div className="space-y-4">
                  <div className="flex space-x-1.5 justify-center">
                    {[...Array(10)].map((_, i) => {
                      const value = i + 1;
                      const isSelected = completedFactors[currentFactor.key] === value;
                      const isSaving = isCurrentSaving;
                      
                      return (
                        <button
                          key={i}
                          type="button"
                          disabled={isSaving || isCurrentSaved}
                          onClick={() => handleFactorSelect(value)}
                          className={`w-8 h-8 rounded-full border-2 text-xs font-medium transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 ${colorClasses.ring} ${
                            isSaving || isCurrentSaved
                              ? isSelected
                                ? `${colorClasses.bg} ${colorClasses.border} text-white shadow-md`
                                : 'opacity-30 cursor-not-allowed border-gray-200 text-gray-300'
                              : isSelected
                                ? `${colorClasses.bg} ${colorClasses.border} text-white shadow-md` 
                                : `border-gray-300 text-gray-500 hover:${colorClasses.border} hover:${colorClasses.text} bg-white`
                          }`}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500 px-1">
                    <span>1 - {currentFactor.lowLabel}</span>
                    <span>10 - {currentFactor.highLabel}</span>
                  </div>

                  {isCurrentSaved && (
                    <div className="text-center">
                      <span className={`inline-flex items-center gap-1.5 text-sm ${colorClasses.text}`}>
                        <CheckCircle className="w-4 h-4" />
                        Saved: {completedFactors[currentFactor.key]}/10
                      </span>
                    </div>
                  )}
                </div>

                {/* Feedback Messages */}
                {isCurrentSaving && (
                  <div className={`flex items-center justify-center space-x-2 ${colorClasses.text} text-sm mt-4`}>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </div>
                )}
                
                {error && !isCurrentSaving && (
                  <div className="flex items-center justify-center space-x-2 text-red-600 text-sm mt-4">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Action Buttons */}
                {!isCurrentSaving && !isCurrentSaved && (
                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      className="bg-white py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      onClick={handleSkipFactor}
                    >
                      {currentStep === 0 ? 'Skip for now' : 'Skip this one'}
                    </button>
                  </div>
                )}

                {/* Completed factors summary (show after first additional factor) */}
                {currentStep > 1 && completedCount > 1 && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="flex flex-wrap gap-1.5 justify-center">
                      {WELLBEING_FACTORS.slice(0, currentStep).map(factor => {
                        const value = completedFactors[factor.key];
                        const factorColors = COLOR_CLASSES[factor.color];
                        if (value === undefined) return null;
                        return (
                          <div 
                            key={factor.key}
                            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${factorColors.text} bg-gray-50`}
                          >
                            {factor.icon}
                            <span>{value}/10</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
